import type { trackEcommerce } from '@svenskaspelab/nova-core/nextjs/analytics';
import type { DialogConfig } from '@svenskaspelab/nova-foundation/nextjs/dialog';
import type { ReactNode } from 'react';

import type { Product } from '../../../../schema/product/entry.js';
import type { WagerId } from '../../../../schema/wager/entry.js';
import { getInstanceClient } from '../../../di/entry.browser.js';
import { PoolbettingError } from '../../../domain/entry.js';
import type { ActionResult } from '../../../lib/entry.js';

type RemoveWagerArgs = {
  wagerId: WagerId;
  price: number;
  product: Product;
  isMarketplace?: boolean;
  showDialog: (props: DialogConfig, meta?: string[]) => Promise<void>;
  callbackOnDone?: () => void;
  trackTransaction?: typeof trackEcommerce;
  icon?: ReactNode;
};

/**
 * Function to remove wager.
 *
 * @param root0 fetch arguments
 * @param root0.wagerId Wager id to remove
 * @param root0.price price of wager to remove
 * @param root0.product product of wager to remove
 * @param root0.isMarketplace whether the wager is a marketplace wager or not
 * @param root0.showDialog Dialog function to show dialog
 * @param root0.icon Icon to show in dialog
 * @param root0.callbackOnDone Callback function to call after successful removal
 * @param root0.trackTransaction optional ecommerce tracking callback
 * @returns void
 */
export async function removeWager({
  wagerId,
  price,
  product,
  isMarketplace,
  showDialog,
  icon,
  callbackOnDone,
  trackTransaction,
}: RemoveWagerArgs): Promise<ActionResult<void>> {
  const removeWagerController = getInstanceClient('IRemoveWagerController');

  try {
    await removeWagerController({
      wagerId,
      price,
      product,
      isMarketplace,
      showDialog,
      callbackOnDone,
      icon,
      trackTransaction,
    });

    return { status: 'SUCCESS', data: undefined };
  } catch (error) {
    if (PoolbettingError.isInstance(error)) {
      return error.toActionResult();
    }

    return { status: 'ERROR', error: 'Det gick inte att annullera ditt spel för tillfället' };
  }
}
